import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Input from "@/components/Usados/InputField";
import Label from "@/components/Usados/Label";
import Button from "@/components/ui/button/Button";
import Alert from "@/components/ui/alert/Alert";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ChevronLeft } from "lucide-react";
import { EyeIcon, EyeCloseIcon } from "@/icons";
import DatePicker from "@/components/Usados/date-picker";
import Rol from "@/assets/roles.json";
import Autocomplete from "@/components/Usados/AutoComplete";
import { useAuth } from "@/context/AuthContext";
import { apiFetch } from "@/service/api";

export default function UpdateUsuario() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    numero_identificacion: "",
    nombres: "",
    apellidos: "",
    correo_electronico: "",
    contrasena: "",
    telefono: "",
    fecha_nacimiento: "",
    rol: "",
  });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchUsuario = async () => {
      try {
        const response = await apiFetch(
          `usuarios/find-one/${id}`,
        );
        if (response.ok) {
          const data = await response.json();
          setForm({
            numero_identificacion: data.numero_identificacion || "",
            nombres: data.nombres,
            apellidos: data.apellidos,
            correo_electronico: data.correo_electronico,
            contrasena: data.contrasena || "",
            telefono: data.telefono || "",
            rol: data.rol? data.rol.toString() : "",
            fecha_nacimiento: data.fecha_nacimiento
          ? data.fecha_nacimiento.split("T")[0]
              : "",
          });
        } else {
          setError("No se pudo cargar la informacion del usuario.");
        }
      } catch (err) {
        console.error("Error fetching usuario:", err);
        setError("Error de conexion al cargar los datos.");
      } finally {
        setLoading(false);
      }
    };
    fetchUsuario();
  }, [id]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setForm((prev) => ({...prev, [name]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    const payload: any = {
   ...form,
      fecha_nacimiento: new Date(form.fecha_nacimiento),
      rol: Number(form.rol),
    };
    try {
      const response = await apiFetch(`usuarios/update/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Error al actualizar el usuario");
      }
      setSuccess(true);
      setTimeout(() => {
        navigate("/seguridad_accesos/usuarios");
      }, 2000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  return (
    <div>
      <PageBreadcrumb pageTitle="Actualizar Usuario" />
      <div className="mb-6">
        <button
          onClick={() => navigate("/seguridad_accesos/usuarios")}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ChevronLeft className="size-5" />
          Volver al listado
        </button>
      </div>
      <div className="min-h-screen rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
        <h3 className="mb-8 font-semibold text-gray-800 text-center text-theme-xl dark:text-white/90 sm:text-2xl">
          Actualizar Informacion de Usuario
        </h3>
        {error && (
          <div className="mb-6">
            <Alert variant="error" title="Error" message={error} />
          </div>
        )}
        {success && (
          <div className="mb-6">
            <Alert
              variant="success"
              title="exito"
              message="Usuario actualizado correctamente. Redirigiendo..."
            />
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-x-6 gap-y-5 lg:grid-cols-2">
            <div>
              <Label>Nombres</Label>
              <Input
                name="nombres"
                type="text"
                placeholder="Ingrese nombres"
                value={form.nombres}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Label>Apellidos</Label>
              <Input
                name="apellidos"
                type="text"
                placeholder="Ingrese apellidos"
                value={form.apellidos}
                onChange={handleChange}
                required
              />
            </div>
            <div className="lg:col-span-2">
              <Label>Correo Electronico</Label>
              <Input
                name="correo_electronico"
                type="email"
                placeholder="usuario@ejemplo.com"
                value={form.correo_electronico}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Label>Numero de Identificacion</Label>
              <Input
                name="numero_identificacion"
                type="text"
                placeholder="Ingrese numero de identificacion"
                value={form.numero_identificacion}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Label>Nueva Contraseña (opcional)</Label>
              <div className="relative">
                <Input
                  name="contrasena"
                  type={showPassword? "text" : "password"}
                  placeholder="Dejar en blanco si no desea cambiar"
                  value={form.contrasena}
                  onChange={handleChange}
                  className="pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 z-30"
                >
                  {showPassword? (
                    <EyeIcon className="size-5 fill-current" />
                  ) : (
                    <EyeCloseIcon className="size-5 fill-current" />
                  )}
                </button>
              </div>
            </div>
            <div>
              <Label>Telefono</Label>
              <Input
                name="telefono"
                type="text"
                placeholder="Ej: +57 300..."
                value={form.telefono}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Label>Fecha de Nacimiento</Label>
              <DatePicker
                id="fecha_nacimiento"
                placeholder="Selecciona una fecha"
                defaultDate={form.fecha_nacimiento}
                onChange={(selectedDates) => {
                  setForm({
                ...form,
                    fecha_nacimiento: selectedDates[0]
                   .toISOString()
                   .split("T")[0],
                  });
                }}
              />
            </div>
            <div className={`${user?.rol === 1? "" : "lg:col-span-2"}`}>
              <Autocomplete
                label="Rol"
                options={Rol.filter((r) => r.id >= (user?.rol || 1)).map(
                  (e) => ({ label: e.name, value: e.id.toString() }),
                )}
                value={form.rol}
                onChange={(id) => setForm({...form, rol: id })}
                placeholder="Seleccione un rol"
              />
            </div>
          </div>
          <div className="mt-10 flex justify-end gap-3">
            <Button
              variant="outline"
              size="md"
              type="button"
              onClick={() => navigate("/seguridad_accesos/usuarios")}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button variant="primary" size="md" type="submit" disabled={saving}>
              {saving? "Guardando..." : "Actualizar Usuario"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}